<template>
    <transition name="light-up-fade">
        <div
            v-if="visible"
            class="light-up-effect"
            @click.stop="skip"
        >
            <!-- 背景 -->
            <div class="effect-bg"></div>
            <div class="effect-vignette"></div>

            <!-- 星空 -->
            <div class="stars">
                <i
                    v-for="item in stars"
                    :key="item.id"
                    class="star"
                    :style="{
                        left: item.left + '%',
                        top: item.top + '%',
                        animationDelay: item.delay + 's',
                        animationDuration: item.duration + 's'
                    }"
                ></i>
            </div>

            <!-- 背景光晕 -->
            <div class="screen-glow"></div>

            <!-- 顶部标题 -->
            <div class="effect-title">
                <div class="title-line title-line-left"></div>

                <div class="title-content">
                    <div class="title-small">ICE CRYSTAL · NEW DISCOVERY</div>
                    <div class="title-main">新的冰块已点亮</div>
                </div>

                <div class="title-line title-line-right"></div>
            </div>

            <!-- 中央舞台 -->
            <div
                class="crystal-stage"
                :class="{
                    'stage-active': stage >= 1,
                    'stage-charge': stage >= 2,
                    'stage-explode': stage >= 3,
                    'stage-reveal': stage >= 4
                }"
            >
                <!-- 扩散光圈 -->
                <div class="energy-ring ring-1"></div>
                <div class="energy-ring ring-2"></div>
                <div class="energy-ring ring-3"></div>
                <div class="energy-ring ring-4"></div>

                <!-- 旋转轨道 -->
                <div class="energy-orbit orbit-1"></div>
                <div class="energy-orbit orbit-2"></div>
                <div class="energy-orbit orbit-3"></div>

                <!-- 底部魔法阵 -->
                <div class="magic-circle">
                    <div class="magic-circle-inner"></div>
                    <div class="magic-ticks"></div>
                </div>

                <!-- 中心光柱 -->
                <div class="light-beam light-beam-main"></div>
                <div class="light-beam light-beam-core"></div>

                <!-- 能量球 -->
                <div class="energy-core"></div>

                <!-- 中央冰晶 -->
                <div class="crystal">
                    <div class="crystal-glow"></div>
                    <div class="crystal-inner"></div>
                    <div class="crystal-face face-1"></div>
                    <div class="crystal-face face-2"></div>
                    <div class="crystal-face face-3"></div>
                    <div class="crystal-shine"></div>

                    <i class="crystal-piece piece-1"></i>
                    <i class="crystal-piece piece-2"></i>
                    <i class="crystal-piece piece-3"></i>
                    <i class="crystal-piece piece-4"></i>
                    <i class="crystal-piece piece-5"></i>
                    <i class="crystal-piece piece-6"></i>
                    <i class="crystal-piece piece-7"></i>
                    <i class="crystal-piece piece-8"></i>
                </div>

                <!-- 爆炸粒子 -->
                <div class="burst-particles">
                    <i
                        v-for="item in particles"
                        :key="item.id"
                        class="particle"
                        :style="{
                            '--x': item.x + 'px',
                            '--y': item.y + 'px',
                            '--size': item.size + 'px',
                            '--delay': item.delay + 's',
                            '--rotate': item.rotate + 'deg'
                        }"
                    ></i>
                </div>

                <!-- NEW 奖励 -->
                <div class="reward-reveal">
                    <div class="new-badge">
                        <span>✦</span>
                        NEW
                        <span>✦</span>
                    </div>

                    <div class="reward-image-box">
                        <div class="reward-image-glow"></div>

                        <img
                            v-if="currentRewardImage"
                            :src="currentRewardImage"
                            alt=""
                        />

                        <div v-else class="default-crystal">
                            <div class="default-crystal-inner"></div>
                        </div>
                    </div>

                    <div class="reward-name">
                        {{ currentRewardName }}
                    </div>

                    <div
                        v-if="newRewardCount > 1"
                        class="reward-count"
                    >
                        本次抽奖点亮 {{ newRewardCount }} 个新冰块
                    </div>
                </div>
            </div>

            <!-- 底部提示 -->
            <div class="effect-bottom">
                <span class="bottom-line"></span>
                <span class="bottom-text">NEW ICE CRYSTAL UNLOCKED</span>
                <span class="bottom-line bottom-line-right"></span>
            </div>

            <div class="skip-tip">
                点击任意位置跳过
            </div>

            <!-- 闪白 -->
            <div class="white-flash"></div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'LightUpEffect',

    props: {
        visible: {
            type: Boolean,
            default: false
        },

        results: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            stage: 0,
            timer: null,
            playing: false,

            stars: [],
            particles: [],

            currentReward: null
        }
    },

    computed: {
        // 只取 repeat:false 的结果
        newRewards() {
            return (this.results || []).filter(item => {
                return item && item.repeat === false
            })
        },

        // 新冰块数量
        newRewardCount() {
            return this.newRewards.length
        },

        // 有些接口没有直接返回图片，所以这里只做兼容
        currentRewardImage() {
            const item = this.currentReward

            if (!item) {
                return ''
            }

            return (
                item.itemImg ||
                item.itemImage ||
                item.rewardImg ||
                item.rewardImage ||
                ''
            )
        },

        currentRewardName() {
            const item = this.currentReward

            if (!item) {
                return '全新冰晶'
            }

            return (
                item.itemName ||
                item.name ||
                item.rewardName ||
                '全新冰晶'
            )
        }
    },

    watch: {
        visible(value) {
            if (value) {
                this.start()
            } else {
                this.stop()
            }
        }
    },

    beforeDestroy() {
        this.stop()
    },

    methods: {
        start() {
            this.stop()

            this.playing = true
            this.stage = 0

            this.createStars()
            this.createParticles()

            // 多个新冰块只取第一个用于中心视觉展示
            this.currentReward = this.newRewards[0] || null

            // 黑场进入
            this.timer = setTimeout(() => {
                if (this.playing) {
                    this.stage = 1
                }
            }, 180)

            // 开始聚能
            this.timer = setTimeout(() => {
                if (this.playing) {
                    this.stage = 2
                }
            }, 900)

            // 爆发
            this.timer = setTimeout(() => {
                if (this.playing) {
                    this.stage = 3
                    this.createParticles()
                }
            }, 1850)

            // NEW出现
            this.timer = setTimeout(() => {
                if (this.playing) {
                    this.stage = 4
                }
            }, 2500)

            // 完成
            this.timer = setTimeout(() => {
                if (this.playing) {
                    this.finish()
                }
            }, 4200)
        },

        createStars() {
            const list = []

            for (let i = 0; i < 90; i++) {
                list.push({
                    id: i,
                    left: Math.random() * 100,
                    top: Math.random() * 100,
                    delay: Math.random() * 2.5,
                    duration: 1.5 + Math.random() * 2.8
                })
            }

            this.stars = list
        },

        createParticles() {
            const list = []

            for (let i = 0; i < 54; i++) {
                const angle = (Math.PI * 2 / 54) * i
                const distance = 180 + Math.random() * 340

                list.push({
                    id: Date.now() + i + Math.random(),
                    x: Math.cos(angle) * distance,
                    y: Math.sin(angle) * distance,
                    size: 3 + Math.random() * 7,
                    delay: Math.random() * 0.15,
                    rotate: Math.random() * 360
                })
            }

            this.particles = list
        },

        skip() {
            if (!this.playing) {
                return
            }

            this.finish()
        },

        finish() {
            if (!this.playing) {
                return
            }

            this.playing = false
            this.stop()

            this.$emit('complete')
        },

        stop() {
            if (this.timer) {
                clearTimeout(this.timer)
                this.timer = null
            }
        }
    }
}
</script>

<style lang="less" scoped>
.light-up-effect {
    position: fixed;
    z-index: 99999;
    inset: 0;

    width: 100vw;
    height: 100vh;

    overflow: hidden;

    display: flex;
    align-items: center;
    justify-content: center;

    background:
        radial-gradient(
            ellipse at 50% 48%,
            rgba(65, 195, 255, 0.28) 0%,
            rgba(17, 78, 145, 0.55) 24%,
            rgba(3, 13, 34, 0.94) 67%,
            #010713 100%
        );

    user-select: none;
    cursor: pointer;
}

.effect-bg {
    position: absolute;
    inset: 0;

    background:
        radial-gradient(
            circle at 50% 50%,
            transparent 0%,
            rgba(0, 14, 39, 0.15) 45%,
            rgba(0, 3, 16, 0.82) 100%
        );
}

.effect-vignette {
    position: absolute;
    inset: 0;

    box-shadow:
        inset 0 0 160px rgba(0, 0, 0, .85),
        inset 0 0 60px rgba(31, 150, 255, .2);

    pointer-events: none;
}

.screen-glow {
    position: absolute;

    width: 80vw;
    height: 80vw;

    max-width: 1000px;
    max-height: 1000px;

    border-radius: 50%;

    background:
        radial-gradient(
            circle,
            rgba(95, 225, 255, .26) 0%,
            rgba(53, 160, 255, .13) 35%,
            transparent 70%
        );

    filter: blur(30px);

    opacity: 0;

    animation: screenGlow 3.5s ease-out forwards;
}

/* =========================
   星空
========================= */

.stars {
    position: absolute;
    inset: 0;

    pointer-events: none;
}

.star {
    position: absolute;

    width: 3px;
    height: 3px;

    border-radius: 50%;

    background: #fff;

    box-shadow:
        0 0 5px #fff,
        0 0 12px rgba(80, 214, 255, .95),
        0 0 20px rgba(46, 150, 255, .7);

    opacity: 0;

    animation: starFlash infinite ease-in-out;
}

/* =========================
   标题
========================= */

.effect-title {
    position: absolute;

    top: 7vh;
    left: 50%;

    transform: translateX(-50%);

    display: flex;
    align-items: center;

    z-index: 50;

    opacity: 0;

    animation: titleIn .8s .3s ease-out forwards;
}

.title-content {
    text-align: center;
    min-width: 380px;
}

.title-small {
    margin-bottom: 7px;

    color: rgba(171, 231, 255, .72);

    font-size: 11px;
    font-weight: 600;

    letter-spacing: 6px;
}

.title-main {
    font-size: 32px;
    font-weight: 900;

    letter-spacing: 8px;

    background:
        linear-gradient(
            180deg,
            #fff 0%,
            #d8f8ff 42%,
            #65d1ff 100%
        );

    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    filter:
        drop-shadow(0 0 8px rgba(93, 218, 255, .9))
        drop-shadow(0 0 22px rgba(41, 143, 255, .7));
}

.title-line {
    width: 110px;
    height: 1px;

    background:
        linear-gradient(
            90deg,
            transparent,
            rgba(125, 226, 255, .95)
        );

    box-shadow:
        0 0 8px rgba(72, 208, 255, .8);
}

.title-line-right {
    transform: rotate(180deg);
}

/* =========================
   舞台
========================= */

.crystal-stage {
    position: relative;

    width: 560px;
    height: 560px;

    display: flex;
    align-items: center;
    justify-content: center;

    z-index: 20;

    transform: scale(.62);
    opacity: .4;

    transition:
        transform .8s cubic-bezier(.18, 1.35, .35, 1),
        opacity .8s ease;
}

.crystal-stage.stage-active {
    transform: scale(1);
    opacity: 1;
}

/* =========================
   魔法阵
========================= */

.magic-circle {
    position: absolute;

    left: 50%;
    top: 50%;

    width: 390px;
    height: 390px;

    transform: translate(-50%, -50%);

    border-radius: 50%;

    border: 1px solid rgba(81, 209, 255, .32);

    box-shadow:
        0 0 20px rgba(56, 186, 255, .28),
        inset 0 0 20px rgba(56, 186, 255, .16);

    opacity: 0;
}

.stage-active .magic-circle {
    opacity: 1;

    animation:
        magicRotate 16s linear infinite,
        magicPulse 2s ease-in-out infinite;
}

.magic-circle::before,
.magic-circle::after,
.magic-circle-inner::before,
.magic-circle-inner::after {
    content: "";

    position: absolute;

    left: 50%;
    top: 50%;

    transform: translate(-50%, -50%);

    border-radius: 50%;
}

.magic-circle::before {
    width: 320px;
    height: 320px;

    border: 1px dashed rgba(99, 218, 255, .42);
}

.magic-circle::after {
    width: 255px;
    height: 255px;

    border: 1px solid rgba(137, 231, 255, .28);
}

.magic-circle-inner::before {
    width: 175px;
    height: 175px;

    border: 1px dashed rgba(255, 255, 255, .4);
}

.magic-circle-inner::after {
    width: 105px;
    height: 105px;

    border: 1px solid rgba(115, 224, 255, .5);
}

.magic-ticks {
    position: absolute;
    inset: 18px;

    border-radius: 50%;

    background:
        repeating-conic-gradient(
            from 0deg,
            rgba(110, 220, 255, .65) 0deg 1deg,
            transparent 1deg 18deg
        );

    -webkit-mask:
        radial-gradient(
            circle,
            transparent 0 46%,
            #000 47% 48%,
            transparent 49%
        );
    mask:
        radial-gradient(
            circle,
            transparent 0 46%,
            #000 47% 48%,
            transparent 49%
        );
}

/* =========================
   能量环
========================= */

.energy-ring {
    position: absolute;

    left: 50%;
    top: 50%;

    transform: translate(-50%, -50%);

    border-radius: 50%;

    border: 1px solid rgba(99, 222, 255, .6);

    box-shadow:
        0 0 12px rgba(53, 195, 255, .65),
        inset 0 0 15px rgba(53, 195, 255, .2);

    opacity: 0;
}

.stage-active .energy-ring {
    opacity: 1;
}

.ring-1 {
    width: 190px;
    height: 190px;

    animation:
        ringPulse 1.6s ease-in-out infinite,
        ringRotate 7s linear infinite;
}

.ring-2 {
    width: 285px;
    height: 285px;

    border-style: dashed;

    animation:
        ringPulse 1.9s .12s ease-in-out infinite,
        ringRotateReverse 10s linear infinite;
}

.ring-3 {
    width: 390px;
    height: 390px;

    border-color: rgba(89, 199, 255, .25);

    animation:
        ringPulse 2.2s .25s ease-in-out infinite,
        ringRotate 14s linear infinite;
}

.ring-4 {
    width: 480px;
    height: 480px;

    border-color: rgba(79, 177, 255, .13);

    animation:
        ringPulse 2.5s .4s ease-in-out infinite,
        ringRotateReverse 18s linear infinite;
}

/* =========================
   轨道
========================= */

.energy-orbit {
    position: absolute;

    width: 420px;
    height: 135px;

    left: 50%;
    top: 50%;

    border-radius: 50%;

    border: 1px solid rgba(108, 222, 255, .52);

    opacity: 0;

    box-shadow:
        0 0 14px rgba(50, 188, 255, .42);
}

.stage-active .energy-orbit {
    opacity: 1;
}

.orbit-1 {
    transform: translate(-50%, -50%) rotate(28deg);
    animation: orbitSpin 4s linear infinite;
}

.orbit-2 {
    transform: translate(-50%, -50%) rotate(-28deg);
    animation: orbitSpinReverse 5s linear infinite;
}

.orbit-3 {
    width: 470px;
    height: 155px;

    transform: translate(-50%, -50%) rotate(78deg);

    border-color: rgba(98, 199, 255, .22);

    animation: orbitSpin 7s linear infinite;
}

/* =========================
   光柱
========================= */

.light-beam {
    position: absolute;

    left: 50%;
    bottom: 50%;

    transform: translateX(-50%);

    pointer-events: none;

    opacity: 0;

    transition:
        height .8s ease,
        opacity .5s ease;
}

.light-beam-main {
    width: 125px;
    height: 0;

    background:
        linear-gradient(
            90deg,
            transparent,
            rgba(87, 219, 255, .25),
            rgba(255, 255, 255, .95),
            rgba(87, 219, 255, .25),
            transparent
        );

    filter: blur(7px);
}

.light-beam-core {
    width: 25px;
    height: 0;

    background:
        linear-gradient(
            90deg,
            transparent,
            #fff,
            transparent
        );

    filter: blur(2px);
}

.stage-active .light-beam-main {
    height: 420px;
    opacity: .72;
}

.stage-active .light-beam-core {
    height: 420px;
    opacity: .9;
}

.stage-explode .light-beam-main {
    height: 850px;
    opacity: 1;

    animation: beamBurst .55s ease-out forwards;
}

.stage-explode .light-beam-core {
    height: 900px;
    opacity: 1;

    animation: beamBurstCore .4s ease-out forwards;
}

/* =========================
   能量核心
========================= */

.energy-core {
    position: absolute;

    width: 85px;
    height: 85px;

    border-radius: 50%;

    background:
        radial-gradient(
            circle,
            #fff 0%,
            #c9f7ff 18%,
            rgba(79, 218, 255, .8) 40%,
            rgba(43, 148, 255, .25) 63%,
            transparent 75%
        );

    filter: blur(1px);

    opacity: 0;

    transform: scale(.3);

    z-index: 5;
}

.stage-charge .energy-core {
    opacity: 1;

    animation:
        coreCharge .9s ease-in-out forwards,
        corePulse .7s 1s ease-in-out infinite;
}

/* =========================
   冰晶
========================= */

.crystal {
    position: relative;

    width: 132px;
    height: 184px;

    z-index: 12;

    filter:
        drop-shadow(0 0 9px #fff)
        drop-shadow(0 0 25px rgba(79, 221, 255, .95))
        drop-shadow(0 0 55px rgba(38, 135, 255, .85));

    animation: crystalFloat 2.5s ease-in-out infinite;
}

.crystal-inner {
    position: absolute;
    inset: 0;

    clip-path: polygon(
        50% 0%,
        78% 17%,
        100% 58%,
        72% 100%,
        50% 82%,
        28% 100%,
        0% 58%,
        22% 17%
    );

    background:
        linear-gradient(
            135deg,
            #fff 0%,
            #e2fbff 13%,
            #75e3ff 35%,
            #159be9 63%,
            #0750a7 100%
        );

    box-shadow:
        inset 0 0 25px rgba(255, 255, 255, .85),
        inset -15px -20px 30px rgba(0, 71, 170, .3);
}

.face-1,
.face-2,
.face-3 {
    position: absolute;

    background: rgba(255, 255, 255, .25);

    pointer-events: none;
}

.face-1 {
    left: 50%;
    top: 7%;

    width: 2px;
    height: 78%;

    transform: rotate(23deg);

    box-shadow: 0 0 8px rgba(255,255,255,.8);
}

.face-2 {
    left: 18%;
    top: 34%;

    width: 62%;
    height: 2px;

    transform: rotate(-14deg);

    box-shadow: 0 0 8px rgba(255,255,255,.6);
}

.face-3 {
    right: 19%;
    top: 42%;

    width: 2px;
    height: 50%;

    transform: rotate(-38deg);

    box-shadow: 0 0 8px rgba(255,255,255,.6);
}

.crystal-shine {
    position: absolute;

    left: -100px;
    top: -80px;

    width: 280px;
    height: 45px;

    transform: rotate(-45deg);

    background:
        linear-gradient(
            90deg,
            transparent,
            rgba(255,255,255,.95),
            transparent
        );

    filter: blur(5px);

    animation: crystalShine 2.1s linear infinite;
}

.crystal-glow {
    position: absolute;

    left: 50%;
    top: 50%;

    width: 180px;
    height: 220px;

    transform: translate(-50%, -50%);

    border-radius: 50%;

    background:
        radial-gradient(
            ellipse,
            rgba(75, 219, 255, .65),
            transparent 65%
        );

    filter: blur(18px);

    z-index: -1;
}

.stage-explode .crystal {
    animation: crystalExplode .65s cubic-bezier(.2,.9,.2,1) forwards;
}

.crystal-piece {
    position: absolute;

    width: 10px;
    height: 30px;

    background:
        linear-gradient(
            180deg,
            #fff,
            #65ddff,
            #1576cf
        );

    box-shadow:
        0 0 8px #59ddff,
        0 0 18px rgba(45, 162, 255, .8);

    opacity: 0;
}

.piece-1 {
    left: -18px;
    top: 32px;
    transform: rotate(-25deg);
}

.piece-2 {
    right: -20px;
    top: 42px;
    transform: rotate(25deg);
}

.piece-3 {
    left: -28px;
    top: 95px;
    transform: rotate(-55deg);
}

.piece-4 {
    right: -28px;
    top: 102px;
    transform: rotate(55deg);
}

.piece-5 {
    left: 28px;
    top: -27px;
    transform: rotate(18deg);
}

.piece-6 {
    right: 25px;
    bottom: -33px;
    transform: rotate(-18deg);
}

.piece-7 {
    left: 5px;
    bottom: 12px;
    transform: rotate(-72deg);
}

.piece-8 {
    right: 5px;
    bottom: 15px;
    transform: rotate(72deg);
}

.stage-explode .crystal-piece {
    animation: pieceExplode .75s ease-out forwards;
}

/* =========================
   爆炸粒子
========================= */

.burst-particles {
    position: absolute;

    left: 50%;
    top: 50%;

    width: 1px;
    height: 1px;

    z-index: 18;

    pointer-events: none;
}

.particle {
    position: absolute;

    left: 0;
    top: 0;

    width: var(--size);
    height: var(--size);

    border-radius: 2px;

    background: #fff;

    box-shadow:
        0 0 6px #fff,
        0 0 15px #54dcff,
        0 0 24px rgba(38, 148, 255, .8);

    opacity: 0;
}

.stage-explode .particle {
    animation:
        particleFly .95s var(--delay) cubic-bezier(.12,.8,.2,1) forwards;
}

/* =========================
   NEW
========================= */

.reward-reveal {
    position: absolute;

    left: 50%;
    top: 50%;

    transform:
        translate(-50%, -50%)
        scale(.15);

    display: flex;
    flex-direction: column;
    align-items: center;

    opacity: 0;

    z-index: 30;

    pointer-events: none;
}

.stage-reveal .reward-reveal {
    animation:
        rewardReveal .72s cubic-bezier(.15,1.35,.3,1) forwards;
}

.new-badge {
    position: absolute;

    top: -65px;

    padding: 8px 28px;

    border-radius: 30px;

    color: #fff;

    font-size: 18px;
    font-weight: 900;

    letter-spacing: 6px;

    background:
        linear-gradient(
            180deg,
            #f4fdff 0%,
            #71dcff 48%,
            #2695ed 100%
        );

    border: 1px solid rgba(255,255,255,.95);

    box-shadow:
        0 0 10px rgba(255,255,255,.9),
        0 0 25px rgba(57,195,255,.95),
        0 0 55px rgba(42,137,255,.7),
        inset 0 0 12px rgba(255,255,255,.9);

    text-shadow:
        0 2px 4px rgba(0, 65, 120, .8);

    white-space: nowrap;
}

.new-badge span {
    margin: 0 8px;

    animation:
        badgeStar 1s ease-in-out infinite alternate;
}

.reward-image-box {
    position: relative;

    width: 185px;
    height: 185px;

    display: flex;
    align-items: center;
    justify-content: center;
}

.reward-image-glow {
    position: absolute;

    width: 170px;
    height: 170px;

    border-radius: 50%;

    background:
        radial-gradient(
            circle,
            rgba(119,234,255,.75),
            rgba(47,158,255,.22) 45%,
            transparent 72%
        );

    filter: blur(10px);

    animation: rewardGlow 1.5s ease-in-out infinite;
}

.reward-image-box img {
    position: relative;

    width: 155px;
    height: 155px;

    object-fit: contain;

    filter:
        drop-shadow(0 0 8px #fff)
        drop-shadow(0 0 22px #58dcff)
        drop-shadow(0 0 45px #258dff);

    animation: rewardFloat 1.7s ease-in-out infinite;
}

.default-crystal {
    position: relative;

    width: 115px;
    height: 155px;

    filter:
        drop-shadow(0 0 15px #6de4ff)
        drop-shadow(0 0 38px #258fff);
}

.default-crystal-inner {
    width: 100%;
    height: 100%;

    clip-path: polygon(
        50% 0,
        80% 19%,
        100% 60%,
        70% 100%,
        50% 82%,
        30% 100%,
        0 60%,
        20% 19%
    );

    background:
        linear-gradient(
            135deg,
            #fff 0%,
            #72e5ff 38%,
            #1269cf 100%
        );

    box-shadow:
        inset 0 0 20px rgba(255,255,255,.8);
}

.reward-name {
    margin-top: 10px;

    color: #fff;

    font-size: 23px;
    font-weight: 900;

    letter-spacing: 5px;

    text-shadow:
        0 0 8px #5cddff,
        0 0 22px #238cff;

    white-space: nowrap;
}

.reward-count {
    margin-top: 10px;

    color: rgba(204, 241, 255, .92);

    font-size: 14px;

    letter-spacing: 3px;

    text-shadow:
        0 0 8px rgba(65, 193, 255, .85);
}

/* =========================
   底部
========================= */

.effect-bottom {
    position: absolute;

    left: 50%;
    bottom: 10vh;

    transform: translateX(-50%);

    display: flex;
    align-items: center;
    gap: 15px;

    z-index: 40;

    opacity: 0;

    animation: bottomIn 1s 1s ease-out forwards;
}

.bottom-text {
    color: rgba(188, 238, 255, .78);

    font-size: 11px;

    letter-spacing: 5px;
}

.bottom-line {
    width: 85px;
    height: 1px;

    background:
        linear-gradient(
            90deg,
            transparent,
            rgba(111, 220, 255, .75)
        );

    box-shadow: 0 0 7px rgba(60, 195, 255, .65);
}

.bottom-line-right {
    transform: rotate(180deg);
}

.skip-tip {
    position: absolute;

    right: 28px;
    bottom: 24px;

    z-index: 50;

    color: rgba(255,255,255,.3);

    font-size: 12px;
}

/* =========================
   闪白
========================= */

.white-flash {
    position: absolute;
    inset: 0;

    background: #fff;

    opacity: 0;

    pointer-events: none;

    z-index: 100;
}

.crystal-stage.stage-explode ~ .white-flash {
    animation: whiteFlash .65s ease-out;
}

/* =========================
   动画
========================= */

@keyframes screenGlow {
    0% {
        opacity: 0;
        transform: scale(.45);
    }

    45% {
        opacity: 1;
    }

    100% {
        opacity: .72;
        transform: scale(1.1);
    }
}

@keyframes starFlash {
    0%,
    100% {
        opacity: 0;
        transform: scale(.35);
    }

    50% {
        opacity: .95;
        transform: scale(1.4);
    }
}

@keyframes titleIn {
    0% {
        opacity: 0;
        transform: translateX(-50%) translateY(-20px);
    }

    100% {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

@keyframes magicRotate {
    from {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

@keyframes magicPulse {
    0%,
    100% {
        filter: brightness(.8);
    }

    50% {
        filter: brightness(1.45);
    }
}

@keyframes ringPulse {
    0%,
    100% {
        transform: translate(-50%, -50%) scale(.9);
        opacity: .28;
    }

    50% {
        transform: translate(-50%, -50%) scale(1.06);
        opacity: .92;
    }
}

@keyframes ringRotate {
    from {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

@keyframes ringRotateReverse {
    from {
        transform: translate(-50%, -50%) rotate(360deg);
    }

    to {
        transform: translate(-50%, -50%) rotate(0deg);
    }
}

@keyframes orbitSpin {
    from {
        transform:
            translate(-50%, -50%)
            rotate(28deg);
    }

    to {
        transform:
            translate(-50%, -50%)
            rotate(388deg);
    }
}

@keyframes orbitSpinReverse {
    from {
        transform:
            translate(-50%, -50%)
            rotate(-28deg);
    }

    to {
        transform:
            translate(-50%, -50%)
            rotate(-388deg);
    }
}

@keyframes coreCharge {
    0% {
        transform: scale(.3);
        filter: blur(6px);
    }

    60% {
        transform: scale(1.25);
        filter: blur(2px);
    }

    100% {
        transform: scale(1);
        filter: blur(1px);
    }
}

@keyframes corePulse {
    0%,
    100% {
        transform: scale(.85);
    }

    50% {
        transform: scale(1.15);
    }
}

@keyframes beamBurst {
    0% {
        opacity: .6;
        filter: blur(10px);
    }

    40% {
        opacity: 1;
        filter: blur(2px);
    }

    100% {
        opacity: 0;
        filter: blur(18px);
    }
}

@keyframes beamBurstCore {
    0% {
        opacity: 1;
    }

    35% {
        opacity: 1;
        transform: translateX(-50%) scaleX(1.8);
    }

    100% {
        opacity: 0;
        transform: translateX(-50%) scaleX(.3);
    }
}

@keyframes crystalFloat {
    0%,
    100% {
        transform:
            perspective(700px)
            rotateX(7deg)
            rotateY(-8deg)
            translateY(0);
    }

    50% {
        transform:
            perspective(700px)
            rotateX(7deg)
            rotateY(8deg)
            translateY(-12px);
    }
}

@keyframes crystalShine {
    0% {
        transform: translateX(-180px) rotate(-45deg);
    }

    100% {
        transform: translateX(250px) rotate(-45deg);
    }
}

@keyframes crystalExplode {
    0% {
        transform: scale(1);
        opacity: 1;
    }

    25% {
        transform: scale(1.18);
        filter:
            brightness(2.2)
            drop-shadow(0 0 40px #fff)
            drop-shadow(0 0 80px #42dfff);
    }

    55% {
        transform: scale(1.05);
        opacity: .85;
    }

    100% {
        transform: scale(.05);
        opacity: 0;
    }
}

@keyframes pieceExplode {
    0% {
        opacity: 0;
        transform: scale(.3);
    }

    25% {
        opacity: 1;
    }

    100% {
        opacity: 0;
        transform:
            translate(
                calc(var(--x, 100px)),
                calc(var(--y, 100px))
            )
            rotate(220deg)
            scale(1.2);
    }
}

@keyframes particleFly {
    0% {
        opacity: 0;
        transform:
            translate(0, 0)
            rotate(0deg)
            scale(.1);
    }

    15% {
        opacity: 1;
    }

    100% {
        opacity: 0;
        transform:
            translate(var(--x), var(--y))
            rotate(var(--rotate))
            scale(1);
    }
}

@keyframes rewardReveal {
    0% {
        opacity: 0;
        transform:
            translate(-50%, -50%)
            scale(.15);
        filter: blur(8px);
    }

    55% {
        opacity: 1;
        transform:
            translate(-50%, -50%)
            scale(1.13);
        filter: blur(0);
    }

    100% {
        opacity: 1;
        transform:
            translate(-50%, -50%)
            scale(1);
        filter: blur(0);
    }
}

@keyframes rewardFloat {
    0%,
    100% {
        transform:
            translateY(0)
            rotate(-2deg);
    }

    50% {
        transform:
            translateY(-8px)
            rotate(2deg);
    }
}

@keyframes rewardGlow {
    0%,
    100% {
        opacity: .45;
        transform: scale(.85);
    }

    50% {
        opacity: 1;
        transform: scale(1.15);
    }
}

@keyframes badgeStar {
    from {
        transform: scale(.8);
        opacity: .65;
    }

    to {
        transform: scale(1.2);
        opacity: 1;
    }
}

@keyframes bottomIn {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(15px);
    }

    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

@keyframes whiteFlash {
    0% {
        opacity: 0;
    }

    20% {
        opacity: .82;
    }

    100% {
        opacity: 0;
    }
}

/* Vue transition */

.light-up-fade-enter-active,
.light-up-fade-leave-active {
    transition: opacity .35s ease;
}

.light-up-fade-enter,
.light-up-fade-leave-to {
    opacity: 0;
}
</style>
